import omit from "lodash/omit";
import range from "lodash/range";
import type { OptionPriceValue } from "../../../admin/products/productsApi";
import type { PricesByDatesProductsAndOptions } from "../../../calculations/calculationVersionEdit/useProductsSellingPrices";
import { useProductsSellingPrices } from "../../../calculations/calculationVersionEdit/useProductsSellingPrices";
import type { Currency } from "../../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import { Price } from "../../../misc/price";
import { getExchangeRate } from "../../../orders/orderEdit/getExchangeRate";
import type { SelectedProduct } from "../SelectedProduct";
import { getProductPrice } from "./getProductPrice";

function getShiftedDate(date: string, dayShift: number) {
  const originalDate = dayjs(date, ISO_DATE_FORMAT);
  const shiftedDate = dayShift < 0 ? originalDate.subtract(-dayShift, "days") : originalDate.add(dayShift, "days");
  return shiftedDate.format(ISO_DATE_FORMAT);
}

export function getSelectedProductPrice(
  selectedProduct: SelectedProduct & { currency?: Currency },
  currency: Currency | undefined,
  margin: number,
  currencyExchangeRate: number | undefined,
  dayShift: number,
  prices: Record<string, { price: OptionPriceValue }>,
): Price {
  const exchangeRate = getExchangeRate(selectedProduct.currency, currency, currencyExchangeRate);
  const options = dayShift === 0 ? selectedProduct.options : selectedProduct.options.map((o) => omit(o, "price"));
  return getProductPrice(options, exchangeRate, margin, prices);
}

function getProductsTotalPrice(
  selectedProducts: (SelectedProduct & { currency: Currency | undefined })[],
  currency: Currency | undefined,
  margin: number,
  currencyExchangeRate: number | undefined,
  dayShift: number,
  prices: PricesByDatesProductsAndOptions,
) {
  const price = selectedProducts
    .map((p) =>
      getSelectedProductPrice(
        p,
        currency,
        margin,
        currencyExchangeRate,
        dayShift,
        (prices[getShiftedDate(p.date, dayShift)] ?? {})[p.id] ?? {},
      ),
    )
    .reduce((prev, curr) => prev.add(curr), new Price(0));
  return price.round(2);
}

export const useProductsTotalPrice = (
  selectedProducts: (SelectedProduct & { currency: Currency | undefined })[],
  currency: Currency | undefined,
  margin: number,
  currencyExchangeRate: number | undefined,
  daysRange: { from: number; to: number },
  participantsNumber: number,
): { prices: Record<number, Price>; isLoading: boolean } => {
  const productsToFetchPricesFor = selectedProducts.map((p) => ({
    id: p.id,
    dateFrom: getShiftedDate(p.date, daysRange.from),
    dateTo: getShiftedDate(p.date, daysRange.to),
  }));
  const { prices, isLoading } = useProductsSellingPrices(productsToFetchPricesFor, participantsNumber);
  const productPrices = Object.fromEntries(
    range(daysRange.from, daysRange.to).map((s) => [
      s,
      getProductsTotalPrice(selectedProducts, currency, margin, currencyExchangeRate, s, prices),
    ]),
  );
  return { prices: productPrices, isLoading };
};
